import { getPromotionData, taskStatuses } from '@frontend/api-client';
import { SwigglyDivider } from '@frontend/web-icons';
import { PageContainer, Text, YStack } from '@frontend/web-react';
import {
  CalendarRecurrencesBanner,
  CappedTasks,
  HeadsUpBanner,
  LetterRecommendationsContainer,
  ReviewTasksContainer,
  ScheduledTasksContainer,
  TrendingTasksContainer,
} from '@frontend/web/components';
import { DirectoryCard } from '@frontend/web/components/DomainSpecific/DirectoryCard';
import {
  useFooterVisibility,
  useListCalendarRecurrencesQuery,
  useListExamplesQuery,
  useListLetterRecommendationsQuery,
  useListRecurrencesQuery,
  useListTasksQuery,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
} from '@frontend/web/hooks';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';

const Home = () => {
  const { data: client, isLoading: isClientLoading } = useRetrieveClientQuery();
  const { data: clientStatus, isLoading: isClientStatusLoading } = useRetrieveClientStatusQuery();
  const { data: tasks, isLoading: isTasksLoading } = useListTasksQuery();
  const { data: letterRecommendations, isLoading: isLetterRecommendationLoading } = useListLetterRecommendationsQuery();
  const { data: recurrences, isLoading: isRecurrencesLoading } = useListRecurrencesQuery();
  const { data: examples, isLoading: isExamplesLoading } = useListExamplesQuery();
  const { data: calendarRecurrences } = useListCalendarRecurrencesQuery();

  const isLoading =
    isClientLoading ||
    isClientStatusLoading ||
    isTasksLoading ||
    isLetterRecommendationLoading ||
    isRecurrencesLoading ||
    isExamplesLoading;

  const { handleScroll, isFooterVisible } = useFooterVisibility();
  const { hasPromotion } = getPromotionData(clientStatus);
  const tasksAwaitingClient = useMemo(
    () => tasks?.filter((task) => taskStatuses['waiting_on_you'].includes(task.status)),
    [tasks],
  );

  return (
    <PageContainer
      contentContainerClassName="pb-lg flex flex-1 justify-between overflow-y-scroll"
      countSkeletons={16}
      isLoading={isLoading}
      isScrollable
      onScroll={handleScroll}
      padding="none"
    >
      <YStack className="gap-xl">
        <YStack>
          {client?.headsUpBanner ? <HeadsUpBanner message={client.headsUpBanner} /> : null}
          <YStack className="gap-md px-md pt-md">
            <Text type="h3">Hi, {client?.firstName}! What can we help you with today?</Text>
            {calendarRecurrences?.length ? <CalendarRecurrencesBanner eventCount={calendarRecurrences.length} /> : null}
          </YStack>
        </YStack>
        {tasksAwaitingClient ? (
          <YStack className="px-md">
            <ReviewTasksContainer tasks={tasksAwaitingClient} />
          </YStack>
        ) : null}
        {recurrences?.length ? <ScheduledTasksContainer recurrences={recurrences} /> : null}
        <YStack className="items-center">
          <SwigglyDivider />
        </YStack>
        {!hasPromotion ? (
          <YStack className="px-md">
            <DirectoryCard />
          </YStack>
        ) : null}
        {letterRecommendations?.length ? (
          <LetterRecommendationsContainer clientStatus={clientStatus} letterRecommendations={letterRecommendations} />
        ) : null}
        {examples?.length && !clientStatus?.tasksRemaining ? (
          <TrendingTasksContainer examples={examples} layout="horizontal" />
        ) : null}
        {clientStatus?.tasksRemaining && clientStatus?.promotionalFeedActive && client && examples ? (
          <CappedTasks clientStatus={clientStatus} examples={examples} />
        ) : null}
        {isFooterVisible ? (
          <YStack className="items-center">
            <Text color="tertiary" type="caption">
              What are you doing all the way down here? Go make a task!
            </Text>
          </YStack>
        ) : null}
      </YStack>
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/home')({
  component: Home,
});
